/**
 * Second half of a WagePro sign-in for an account with two-step verification
 * on: `wageproAuth.login` verified the password and returned a short-lived
 * pending token instead of a session. The user types the six-digit code from
 * their authenticator app — or switches to a recovery code — and
 * `wageproTwoFactor.verifyLogin` issues the session.
 *
 * Rendered inside the login screen's card (it owns no page chrome).
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, FormField, Input } from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { isNative } from '../../lib/platform.js';
import {
  nativeClientInput,
  type NativeSessionCarrier,
} from '../../lib/auth/native_shell_auth.js';
import {
  classifyTwoFactorError,
  isSubmittableCode,
  normaliseTwoFactorCode,
  type TwoFactorErrorKind,
} from '../../lib/auth/two_factor_logic.js';
import { TwoFactorError } from '../../components/auth/two_factor_error.js';
import { finishSignIn } from './finish_sign_in.js';

type Mode = 'authenticator' | 'recovery';

export function TwoFactorCodeStep({
  pendingToken,
  onSignedIn,
  onStartOver,
}: {
  pendingToken: string;
  onSignedIn?: () => void;
  /** Back to the email/password form (the pending token expires after 5 minutes). */
  onStartOver: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [mode, setMode] = useState<Mode>('authenticator');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<TwoFactorErrorKind | null>(null);

  async function handleSubmit(): Promise<void> {
    if (!isSubmittableCode(code, mode)) {
      setError('invalid_code');
      return;
    }
    setSubmitting(true);
    setError(null);
    // Only the token field is read here — see `wagepro_login_screen.tsx`.
    let result: NativeSessionCarrier;
    try {
      result = await getTrpcClient().wageproTwoFactor.verifyLogin.mutate({
        pending_token: pendingToken,
        code: normaliseTwoFactorCode(code),
        ...nativeClientInput(isNative()),
      });
    } catch (err) {
      setSubmitting(false);
      setError(classifyTwoFactorError(err));
      return;
    }

    const outcome = await finishSignIn(result, onSignedIn);
    if (outcome === 'session_not_stored') {
      setSubmitting(false);
      setError('unknown');
    }
  }

  const expired = error === 'expired';

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-[color:var(--ink)]">
          {t('two_factor.code_title')}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
          {mode === 'authenticator'
            ? t('two_factor.code_dek')
            : t('two_factor.recovery_code_dek')}
        </p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <FormField
          label={
            mode === 'authenticator'
              ? t('two_factor.code_label')
              : t('two_factor.recovery_code_label')
          }
          htmlFor="two-factor-code"
        >
          <Input
            id="two-factor-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode={mode === 'authenticator' ? 'numeric' : 'text'}
            autoFocus
            disabled={submitting || expired}
            className="mono"
          />
        </FormField>

        {error !== null && <TwoFactorError kind={error} />}

        {expired ? (
          <Button
            type="button"
            variant="primary"
            onClick={onStartOver}
            className="w-full justify-center"
          >
            {t('two_factor.start_over')}
          </Button>
        ) : (
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            className="w-full justify-center"
          >
            {submitting ? t('two_factor.verifying') : t('two_factor.verify')}
          </Button>
        )}
      </form>

      <div className="flex flex-col items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setMode(mode === 'authenticator' ? 'recovery' : 'authenticator');
            setCode('');
            setError(null);
          }}
          disabled={submitting}
        >
          {mode === 'authenticator'
            ? t('two_factor.use_recovery_code')
            : t('two_factor.use_authenticator')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onStartOver}
          disabled={submitting}
        >
          {t('two_factor.back_to_sign_in')}
        </Button>
      </div>
    </div>
  );
}
